/* eslint-disable no-return-assign, no-param-reassign, arrow-body-style, react/sort-comp, array-callback-return */
import classnames from 'classnames';
import React from 'react';
import ReactDOM from 'react-dom';
import DropZone from 'es6/components/apps/sentence-builder/drop-zone';
import ShiftZone from 'es6/components/apps/sentence-builder/shift-zone';
import WordSlot from 'es6/models/word-slot';
import WordPiece from 'es6/components/apps/sentence-builder/word-piece';

/**
 * hideWordSlotIndex: the index of the word slot that will be taken
 *                    off in the hypothetical event of a shift event.
 * shiftLeftSide:    if the target is being shifted on the left or right.
 * shiftEventTarget: the DOM node that triggered the shift event.
 */
const INITIAL_STATE = {
  hideWordSlotIndex: -1,
  shiftLeftSide: false,
  shiftEventTarget: null,
};

class WordSlots extends React.Component {
  constructor(props) {
    super(props);
    this.state = INITIAL_STATE;
  }

  componentDidMount() {
    this._positionWordSlots();
  }

  /**
   * Make sure that any DOM nodes that were manipulated get set back to normal.
   *
   * @param {Object} nextProps
   * @param {Object} nextState
   */
  componentWillUpdate(nextProps, nextState) {
    const { shiftEventTarget, hideWordSlotIndex } = this.state;

    // Make sure the hidden slot is shown.
    if (hideWordSlotIndex !== -1 && nextState.hideWordSlotIndex !== hideWordSlotIndex) {
      ReactDOM.findDOMNode(this).childNodes[hideWordSlotIndex].style.display = '';
    }

    // Make sure the padding is removed from the existing shift event target.
    if (shiftEventTarget && nextState.shiftEventTarget !== shiftEventTarget) {
      shiftEventTarget.style.paddingLeft = '';
      shiftEventTarget.style.paddingRight = '';
    }
  }

  componentDidUpdate() {
    this._positionWordSlots();
  }

  /**
   * Helper method to determine if the slot is draggable.
   *
   * @param {WordSlot} slot
   * @returns {Boolean}
   */
  _isDraggable(slot) {
    return !slot.isPunctuation && this.props.enableDragEvents && !slot.isCorrectAndGraded();
  }

  /**
   * Determines which slot will get dropped out, only update the state
   * if the end result will be different.
   *
   * @param {Number} slotIndex
   * @param {Boolean} shiftLeftSide
   * @param {WordChoice} wordChoice
   * @param {Event} e
   */
  _handleShiftStart(slotIndex, shiftLeftSide, wordChoice, e) {
    // In case the word piece is targeted, make sure the parent gets chosen.
    const { target } = e;
    const shiftEventTarget = target.classList.contains('shift-zone') ? target : target.parentElement;

    if (this.state.hideWordSlotIndex === -1 || shiftLeftSide !== this.state.shiftLeftSide) {
      const wordSlots = this.props.data;
      const hideWordSlotIndex = WordSlot.getShiftEndIndex(wordSlots, slotIndex);
      this.setState({ shiftLeftSide, shiftEventTarget, hideWordSlotIndex });
    }
  }

  /**
   * Pass the arguments back up and reset the hideWordSlotIndex state.
   */
  _handleShift(...args) {
    this.props.onShift.call(this, ...args);
    this._resetState();
  }

  /**
   * Reset the hideWordSlotIndex state.
   */
  _handleShiftEnd() {
    this._resetState();
  }

  /**
   * Calculates and positions the nodes within the word slot container.
   */
  _positionWordSlots() {
    const domNode = ReactDOM.findDOMNode(this);
    const wordSlots = [...domNode.childNodes];
    const totalWidth = domNode.clientWidth;
    const { hideWordSlotIndex, shiftLeftSide, shiftEventTarget } = this.state;

    // Reset the padding on the shift event target so we can recalculate
    // positions, naturally.
    if (shiftEventTarget) {
      shiftEventTarget.style.paddingLeft = '';
      shiftEventTarget.style.paddingRight = '';
    }

    // Get a reference to the hidden word slot (in case we need its stored width).
    const hiddenWordSlot = wordSlots[hideWordSlotIndex];

    // Reset the display state of the word slots.
    wordSlots.forEach(wordSlot => wordSlot.style.display = '');

    // Get the row height by finding the largest client height.
    const rowHeight = wordSlots.reduce((prev, wordSlot) => {
      return Math.max(prev, wordSlot.clientHeight);
    }, 0);

    // Keep track of the offsets.
    let leftOffset = 0;
    let topOffset = 0;

    // Iterate through the word slots.
    wordSlots.forEach((wordSlot, i) => {
      // If the word slot is the hidden element, set the display to none
      if (i === hideWordSlotIndex) {
        wordSlot.style.display = 'none';
        return;
      }

      // Is this the shifting element?
      const isShifting = wordSlot.contains(shiftEventTarget);

      // Determine the left offset for this element.
      let left = leftOffset;

      // If a word is shifting on the line below, do not bring it up to the top.
      // Moving to the line above will trigger a dragleave event.
      const shiftingBelow = isShifting && wordSlot._storedValues.offsetTop !== topOffset;

      // Can the word fit?
      const canFit = leftOffset + wordSlot.clientWidth < totalWidth;

      // If the word is shifting below or can't fit, put it on a new line.
      if (shiftingBelow || !canFit) {
        left = 0;
        topOffset += rowHeight;
      }

      // Set the left and top position on each DOM element.
      wordSlot.style.left = left > 0 ? `${left}px` : '0';
      wordSlot.style.top = topOffset > 0 ? `${topOffset}px` : '0';

      // Keep a record of what the previous values were. This can be useful
      // during a shift event.
      if (!shiftEventTarget) {
        const { offsetLeft, offsetTop, clientWidth } = wordSlot;
        wordSlot._storedValues = { offsetLeft, offsetTop, clientWidth };
      }

      // If the element shifting, adjust the padding so it matches the
      // difference offsets from before. If the top offset differs or the left offset
      // does not differ, simply allow the default CSS value to be used.
      if (isShifting) {
        // Determine if the shift is happening on the left or right side.
        const styleAttr = shiftLeftSide ? 'paddingLeft' : 'paddingRight';

        // If the left offset has changed, then that is the value to use.
        // If not, then use the client width of the hidden word slot so
        // the dimensions don't change. The padding should also remain
        // the same, so use the computed padding style.
        const computedStyle = window.getComputedStyle(shiftEventTarget);
        const existingPadding = parseInt(computedStyle[styleAttr], 10);
        const adjustedOffset = Math.abs(left - wordSlot._storedValues.offsetLeft);

        const newPadding = existingPadding +
          (adjustedOffset === 0 ? hiddenWordSlot._storedValues.clientWidth : adjustedOffset);

        // Actually set the new padding.
        shiftEventTarget.style[styleAttr] = `${newPadding}px`;
      }

      // Adjust the offset values based on the new word slot width.
      leftOffset = left + wordSlot.clientWidth;
    });
  }

  render() {
    const dropZones = [];

    // Render slots as normal, add drop zones when previous slot
    // hasn't been graded.
    this.props.data.map((slot, i) => {
      const renderedSlot = this._renderSlot(slot, i);
      dropZones.push(renderedSlot);
    });

    // Since all word slots are absolutely positioned, be sure that
    // the position for the actual container is relative.
    const style = {
      position: 'relative',
    };

    return (
      <section className="word-slots" style={style}>{dropZones}</section>
    );
  }

  _renderSlot(slot, i) {
    // Set the position to be absolutely positioned (will calculate)
    // the positions manually.
    const style = {
      position: 'absolute',
    };

    if (slot.isPunctuation) {
      return (<span key={i} className="punctuation" style={style}>{slot.expected}</span>);
    }

    const dropZoneProps = {
      className: classnames({
        'word-slot': true,
        filled:      slot.isFilled(),
        correct:     slot.isCorrect(),
        incorrect:   slot.isIncorrect(),
        corrected:   slot.corrected,
        graded:      slot.graded,
        hinted:      slot.hinted,
      }),
      disabled: slot.isCorrectAndGraded(),
      expectedValue: slot.expected,
    };

    const wordPieceProps = {
      onDrag: this.props.onDrag,
      onDragEnd: this.props.onDragEnd,
    };

    // Determine if this word slot can be shifted and if so, will the removed
    // block be to the left or right?
    const shiftEndIndex = WordSlot.getShiftEndIndex(this.props.data, i);
    const isShiftable = slot.isFilled() && shiftEndIndex !== -1;

    // Corrected slots are no longer draggable.
    if (this._isDraggable(slot)) {
      if (this.props.onClick) {
        Object.assign(wordPieceProps, {
          onClick: this.props.onClick,
          enableDragEvents: true,
          wordChoice: slot.wordChoice,
        });
      }

      if (isShiftable) {
        // Will the shift zone be to the left or right?
        const shiftsRight = shiftEndIndex > i;

        Object.assign(dropZoneProps, {
          onDragLeave: this._handleShiftEnd.bind(this, i),
          onDragLeft: this._handleShiftStart.bind(this, i, true),
          onDragRight: this._handleShiftStart.bind(this, i, false),
          onDropLeft: this._handleShift.bind(this, shiftsRight ? i : i - 1),
          onDropRight: this._handleShift.bind(this, shiftsRight ? i + 1 : i),
        });
      } else if (this.props.onDrop) {
        Object.assign(dropZoneProps, {
          onDrop: this.props.onDrop.bind(null, slot),
        });
      }
    }

    const wordPiece = this._renderWordPiece(wordPieceProps, slot.value);

    return (
      <div key={i} className="word-slot-container" style={style}>
        {
          isShiftable ?
            this._renderShiftZone(dropZoneProps, wordPiece) :
            this._renderDropZone(dropZoneProps, wordPiece)
        }
      </div>
    );
  }

  _renderDropZone(props, wordPiece) {
    return (<DropZone {...props}>{wordPiece}</DropZone>);
  }

  _renderShiftZone(props, wordPiece) {
    return (<ShiftZone {...props}>{wordPiece}</ShiftZone>);
  }

  _renderWordPiece(props, value) {
    return (<WordPiece {...props}>{value}</WordPiece>);
  }

  _resetState() {
    this.setState(INITIAL_STATE);
  }
}

WordSlots.propTypes = Object.freeze({
  data: React.PropTypes.array.isRequired,
  enableDragEvents: React.PropTypes.bool,
  onClick: React.PropTypes.func,
  onDrag: React.PropTypes.func,
  onDragEnd: React.PropTypes.func,
  onDrop: React.PropTypes.func,
  onShift: React.PropTypes.func,
});

export default WordSlots;
